import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Mail, User } from "lucide-react";

export default function NewsletterForm() {
  const { t, tRaw } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = t("newsletter.validation.required");
    if (!values.email.trim()) {
      next.email = t("newsletter.validation.required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      next.email = t("newsletter.validation.email");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    navigate("/thank_you");
  };

  const labels = tRaw("newsletter.labels") || {};
  const placeholders = tRaw("newsletter.placeholders") || {};

  return (
    <form
      onSubmit={onSubmit}
      data-testid="newsletter-form"
      noValidate
      className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 sm:p-8"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="newsletter-name"
            className="block text-xs font-semibold uppercase tracking-widest text-slate-700 mb-1.5"
          >
            {labels.name}
          </label>
          <div className="relative">
            <User
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              id="newsletter-name"
              type="text"
              data-testid="newsletter-name-input"
              value={values.name}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
              placeholder={placeholders.name}
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-300 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F2C59] focus:border-transparent"
            />
          </div>
          {errors.name && (
            <p
              data-testid="newsletter-name-error"
              className="text-xs text-red-600 mt-1"
            >
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="newsletter-email"
            className="block text-xs font-semibold uppercase tracking-widest text-slate-700 mb-1.5"
          >
            {labels.email}
          </label>
          <div className="relative">
            <Mail
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              id="newsletter-email"
              type="email"
              data-testid="newsletter-email-input"
              value={values.email}
              onChange={(e) => setValues({ ...values, email: e.target.value })}
              placeholder={placeholders.email}
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-300 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F2C59] focus:border-transparent"
            />
          </div>
          {errors.email && (
            <p
              data-testid="newsletter-email-error"
              className="text-xs text-red-600 mt-1"
            >
              {errors.email}
            </p>
          )}
        </div>
      </div>
      <button
        type="submit"
        data-testid="newsletter-submit-button"
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center bg-[#064E3B] hover:bg-[#0d6b52] text-white font-semibold px-6 py-3 rounded-md text-sm tracking-wide"
      >
        {t("newsletter.submit")}
      </button>
      <p className="mt-4 text-xs text-slate-500 leading-relaxed">
        {t("newsletter.consent")}
      </p>
    </form>
  );
}
